import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff, User, Music } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscribersSignup() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Prefill from an emailed claim link (?claim=<token>&email=<email>)
  const claimToken = params.get("claim") || "";
  useEffect(() => {
    const e = params.get("email");
    if (e) setEmail(e);
  }, [params]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter your email and a password");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribers/register`, {
        email,
        password,
        display_name: displayName || undefined,
        claim_token: claimToken || undefined,
      });
      // Session is set as an httpOnly cookie by the server; load the profile.
      const user = await refresh();
      const granted = (user?.subscription_status === "active") || (user?.purchased_items && user.purchased_items.length > 0);
      toast.success(granted
        ? "Account created — your access is unlocked!"
        : "Account created! If you don't see your purchase, make sure you used the same email as checkout.");
      navigate("/portal");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-4 py-12" data-testid="subscribers-signup-page">
      <Card className="w-full max-w-md card-official">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-royal-purple/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Music className="w-8 h-8 text-royal-purple" />
          </div>
          <CardTitle className="heading-card text-royal-blue">Claim Your Purchases &amp; Access</CardTitle>
          <CardDescription>
            Create your account to unlock the subscription and/or downloads you just purchased.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-kingdom-gold/10 border border-kingdom-gold/30 rounded p-3 mb-4">
            <p className="text-xs text-slate-700 text-center">
              Use the <strong>same email</strong> you entered at Stripe checkout, and your
              subscription and downloads will be unlocked automatically.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="form-label">Email (used at checkout)</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input pl-10"
                  placeholder="you@example.com"
                  data-testid="signup-email-input"
                  style={{ fontSize: "16px" }}
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <Label className="form-label">Display Name (optional)</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="form-input pl-10"
                  placeholder="Your name"
                  data-testid="signup-name-input"
                  style={{ fontSize: "16px" }}
                />
              </div>
            </div>

            <div>
              <Label className="form-label">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pl-10 pr-10"
                  placeholder="Create a password"
                  data-testid="signup-password-input"
                  style={{ fontSize: "16px" }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Min 8 characters, 1 uppercase &amp; 1 special character.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full btn-gold"
              disabled={loading}
              data-testid="signup-submit-btn"
            >
              {loading ? "Creating account..." : "Create Account & Claim Access"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-slate-600 text-sm">
              Already have an account?{" "}
              <Link to="/subscribers/login" className="text-royal-purple font-semibold underline" data-testid="go-to-login-link">
                Log in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
